<script setup>
import moment from 'moment'
import router from '@/router'
import { getI18n } from '@/plugins/i18n'
import { menuRoutesEnum } from '@/router'
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

import { DxScrollView } from 'devextreme-vue/scroll-view'
import { DxSortable } from 'devextreme-vue/sortable'
import { DxDateRangeBox } from 'devextreme-vue/date-range-box'
import { DxTextBox } from 'devextreme-vue/text-box'
import { DxSelectBox } from 'devextreme-vue/select-box'

import { useWebStreamWs } from '@/ws/web-stream.ws'

import { useGarageStore } from '@/stores/garage.store'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

import { useNotify } from '@/composables/notify'
import { useThrottle } from '@/composables/debounce'
import { customerDisplayExpr } from '@/composables/label'
import { useWebSocket } from '@/composables/web-socket'
import { useDocumentService } from '@/services/document.service'
import { useDebounce } from '@/composables/debounce'

import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import { useWebSiteStore } from '@/stores/web-site.store'

const i18n = getI18n()
const garageStore = useGarageStore()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const { notifyWarning, notifyError } = useNotify()
const { extractReferenceData } = useDocumentService()

const webStreamWs = useWebStreamWs()
const { createSocket } = useWebSocket()
const kanbanWebSocket = createSocket(webStreamWs.KANBAN_UPDATE_STREAM)

const statuses = garageStore.getLocalizedDocumentProgressStatuses().filter(s => s.visible && s.type !== portalStore.documentProgressStatusesEnum.PLANNED)

const lists = ref(
  statuses.map((status) => ({ id: status.id, name: status.name, color: status.color, events: [], collapsed: webSiteStore.kanbanCollapsedStatuses.includes(status.id) }))
)
const events = ref([])

const minDateRange = moment().subtract(3, 'months').toDate()

const dateRange = ref([moment().subtract(1, 'months').toDate(), null])
const lastUpdatedAt = ref(null)

const enrichWorksheet = (worksheet) => {
  worksheet.vehicle = registryStore.vehicles.find(v => v.id === worksheet.vehicle_id)
  worksheet.supplier_brand_ref = worksheet.vehicle?.supplier_brand_ref
  worksheet.supplierBrand = registryStore.supplierBrands.find(i => i.id === worksheet.vehicle?.supplier_brand_ref)
  worksheet.customer = registryStore.customers.find(c => c.id === worksheet.customer_id)
  worksheet.customer_name_name_ext_vat = worksheet.customer ? `${worksheet.customer.name} ${worksheet.customer.name_ext ? '(' + worksheet.customer.name_ext + ')' : ''} ${worksheet.customer.vat_number ? '- ' + worksheet.customer.vat_number : ''}` : ''
  return `${worksheet.vehicle?.plate_number} ${worksheet.supplierBrand ? worksheet.supplierBrand.code : ''} ${worksheet.vehicle?.name}`
}

const updateEventsAsync = async() => {
  if (dateRange.value[0] === null) {
    dateRange.value[0] = minDateRange
  }

  let filter = `created_at ge '${dateRange.value[0].toISOString()}'`
  if (dateRange.value[1]) {
    const endDatetime = moment(dateRange.value[1]).endOf('day')
    filter += ` and created_at le '${endDatetime.toISOString()}'`
  }
  if (searchText.value) {
    filter += ` and contains(tolower(search_field), '${searchText.value.toLowerCase().replace(/'/g, "''")}')`
  }

  const orderBy = sortFilter.value === 1 ? 'order' : 'created_at'

  events.value = (await garageStore.api.getWorksheetsAsync({'$filter': filter, '$orderby': orderBy})).data

  lastUpdatedAt.value = (await garageStore.api.getWorksheetsLastUpdateAsync())?.data

  lists.value.forEach(l => {
    l.events = events.value.filter((event) => event.document_progress_status === l.id).map((event) => {
      const filterField = enrichWorksheet(event)

      return {
        title: `${event.vehicle?.plate_number} - ${event.supplierBrand ? event.supplierBrand.code : ''} - ${event.vehicle?.name}`,
        subTitle: event.customer?.name,
        customerName: customerDisplayExpr(event.customer),
        intervals: event.garage_events.sort((a, b) => new Date(b.start) - new Date(a.start)),
        expectedDeliveryDate: event.expected_delivery_date ? moment(event.expected_delivery_date).format('DD/MM') : null,
        visible: true,
        filterField,
        data: event
      }
    })
  })
}

const processWorksheet = (worksheet, previous) => {
  const mainIndex = events.value.findIndex(e => e.id === worksheet.id)

  if (mainIndex !== -1) {
    Object.assign(events.value[mainIndex], worksheet)
  } else {
    events.value.push(worksheet)
  }

  lists.value.forEach(l => {
    if (previous?.document_progress_status === l.id && worksheet.document_progress_status !== l.id) {
      const eventIndex = l.events.findIndex(e => e.data.id === worksheet.id)
      if (eventIndex !== -1) {
        l.events.splice(eventIndex, 1)
      }
      return
    }

    if (worksheet.document_progress_status !== l.id) {
      return
    }

    const eventIndex = l.events.findIndex(e => e.data.id === worksheet.id)
    const filterField = enrichWorksheet(worksheet)
    const newEvent = {
      title: `${worksheet.vehicle?.plate_number} - ${worksheet.supplierBrand ? worksheet.supplierBrand.code : ''} - ${worksheet.vehicle?.name}`,
      subTitle: worksheet.customer?.name,
      customerName: customerDisplayExpr(worksheet.customer),
      intervals: worksheet.garage_events.sort((a, b) => new Date(b.start) - new Date(a.start)),
      expectedDeliveryDate: worksheet.expected_delivery_date ? moment(worksheet.expected_delivery_date).format('DD/MM') : null,
      visible: filterField.toLowerCase().includes(searchText.value.toLowerCase()) || worksheet.customer_name_name_ext_vat.toLowerCase().includes(searchText.value.toLowerCase()),
      filterField,
      data: worksheet
    }

    if (eventIndex !== -1) {
      Object.assign(l.events[eventIndex], newEvent)
    } else {
      newEvent.data = { ...worksheet, order: worksheet.order ?? l.events.length }
      l.events.push(newEvent)
    }

    l.events.sort((a, b) => (a.data.order ?? 0) - (b.data.order ?? 0))
  })
}

const onMessageReceive = (sender, response, message) => {
  if (!message.data?.worksheet) { return }

  lastUpdatedAt.value = message.data.last_updated_at

  const updatedWorksheet = message.data.worksheet
  const previous = structuredClone(events.value.find(e => e.id === updatedWorksheet.id))
  processWorksheet(updatedWorksheet, previous)

  const knownIds = new Set(events.value.map(e => e.id))

  const modifiedWorksheets = message.data.modified_worksheets || []
  modifiedWorksheets.filter(ws => knownIds.has(ws.id))
    .forEach(ws => {
      const mainIndex = events.value.findIndex(e => e.id === ws.id)
      if (mainIndex !== -1) {
        events.value[mainIndex].order = ws.order
      }

      const list = lists.value.find(l => l.id === ws.document_progress_status)
      if (!list) return

      const eventIndex = list.events.findIndex(e => e.data.id === ws.id)
      if (eventIndex !== -1) {
        list.events[eventIndex].data.order = ws.order
        list.events.sort((a, b) => (a.data.order ?? 0) - (b.data.order ?? 0))
      }
    })
}

onMounted(async() => {
  await updateEventsAsync()
  await kanbanWebSocket.subscribe(onMessageReceive)
})
onUnmounted(() => kanbanWebSocket.unsubscribe())

// Re-enable this to re-order lists
// const onListReorder = (e) => {
//   const list = lists.value.splice(e.fromIndex, 1)[0]
//   lists.value.splice(e.toIndex, 0, list)

//   const status = statuses.splice(e.fromIndex, 1)[0]
//   statuses.splice(e.toIndex, 0, status)
// }

const throttledWarning = useThrottle(() => {
  notifyWarning(i18n.t('warning.kanbanBoard.invoicedTaskWarning'))
}, 500)

const onTaskDragStart = (e) => {
  e.cancel = isInvoicedStatus(e.fromData[e.fromIndex].data.document_progress_status)
  if (e.cancel) {
    throttledWarning()
  }
}

const getVisibleToIndex = (items, toIndex) => {
  let index = toIndex
  while (index < items.length && !items[index].visible) { index++ }
  return index
}

const onTaskDragEnd = async(e) => {
  const toStatusId = e.toComponent.instance().option('key')
  if (isInvoicedStatus(toStatusId)) { // add logic here to check if generated document exists
    notifyWarning(i18n.t('warning.kanbanBoard.missingGeneratedDocument'))
    e.cancel = true
    return
  }
  const sameList = e.fromComponent === e.toComponent
  if (sameList && e.toIndex === e.fromIndex) { return }

  const itemData = e.fromData.splice(e.fromIndex, 1)[0]
  itemData.data.document_progress_status = toStatusId

  const toIndex = getVisibleToIndex(e.toData, e.toIndex)
  const movingDown = sameList && e.toIndex > e.fromIndex
  const successor = movingDown ? null : e.toData.slice(toIndex).find(i => i.visible)
  const predecessor = successor ? null : e.toData.slice(0, toIndex).findLast(i => i.visible)
  e.toData.splice(toIndex, 0, itemData)

  nextTick(async() => {
    try {
      await garageStore.api.updateWorksheetAsync(itemData.data.id, {
        order_before_id: successor?.data.id ?? null,
        order_after_id: predecessor?.data.id ?? null,
        document_progress_status: itemData.data.document_progress_status,
        last_updated_at: lastUpdatedAt.value,
        kanban: true
      }, {
        skipErrorMessage: (e) => (e.response?.data?.message === 'error.obsolete_data')
      })
    } catch {
      notifyError(i18n.t('error.obsolete_data'), {
        timeout: 0,
        actions: [
          {
            icon: 'mdi-refresh',
            color: 'white',
            handler: async() => await updateEventsAsync()
          }
        ]
      })
    }
  })
}

const isInvoicedStatus = (status) => (garageStore.documentProgressStatuses.find(s => s.id === status && s.visible)?.type === portalStore.documentProgressStatusesEnum.INVOICED)

const columnWidth = computed(() => {
  const expandedColumns = lists.value.filter(i => !i.collapsed).length || 1
  return `${100 / expandedColumns}%`
})

const getReferenceLinkTarget = () => { return menuRoutesEnum.WORKSHEETS }

const filterSortChanged = ref(false)
const dateRangeFilter = ref(null)
const searchText = ref('')
const sortFilter = ref(1)

const onCompanyDataClick = () => {
  const target = menuRoutesEnum.COMPANY
  const route = { path: target, query: { tab: 3 } }
  setTimeout(() => router.push(route), 500)
}

const onDateRangeInitialized = (e) => {
  dateRangeFilter.value = e.component
}

const onDateRangeValueChanged = (e) => {
  filterSortChanged.value = true
  updateEventsAsync()
}

let pendingSearchText = ''
const debouncedSearch = useDebounce(async() => {
  searchText.value = pendingSearchText
  await updateEventsAsync()
})
const onSearchTextChanged = (e) => {
  pendingSearchText = e.value
  filterSortChanged.value = true
  debouncedSearch()
}

const onClearFilter = () => {
  dateRangeFilter.value.reset()
  pendingSearchText = ''
  searchText.value = ''
  sortFilter.value = 1
  filterSortChanged.value = false
  lists.value.forEach(list => {
    list.events.forEach(event => {
      event.visible = true
    })
  })
}

const sortOptions = [
  { text: i18n.t('common.sortByDate'), value: 0 },
  { text: i18n.t('common.sortByPriority'), value: 1 },
]

const onSortFilterChanged = async() => {
  filterSortChanged.value = true
  await updateEventsAsync()
}

const onCollapsedStatus = (list) => {
  list.collapsed = !list.collapsed
  webSiteStore.kanbanCollapsedStatuses = list.collapsed ? [...webSiteStore.kanbanCollapsedStatuses, list.id] : webSiteStore.kanbanCollapsedStatuses.filter(id => id !== list.id)
}

</script>
<template>
  <div>
    <div class="header">
      <dx-text-box
        :value="searchText"
        value-change-event="input"
        :placeholder="i18n.t('common.plateBrandOrCustomerName')"
        width="15rem"
        @value-changed="onSearchTextChanged"
      />
      <dx-date-range-box
        v-model:value="dateRange"
        class="q-ml-md"
        :min="minDateRange"
        :start-date="dateRange[0]"
        @initialized="onDateRangeInitialized"
        @value-changed="onDateRangeValueChanged"
      />
      <dx-select-box
        v-model="sortFilter"
        class="q-ml-md"
        :data-source="sortOptions"
        display-expr="text"
        value-expr="value"
        @value-changed="onSortFilterChanged"
      />
      <q-btn
        class="q-ml-md"
        icon="mdi-filter-off"
        size=".7rem"
        :disabled="!filterSortChanged"
        round
        flat
        @click="onClearFilter"
      />
      <q-btn
        class="q-ml-md"
        icon="mdi-cog"
        size=".7rem"
        round
        flat
        @click="onCompanyDataClick"
      />
    </div>
    <div id="kanban">
      <!-- <DxScrollView
        class="scrollable-board"
        direction="horizontal"
        show-scrollbar="always"
      >
        <DxSortable
          style="display: flex;"
          class="sortable-lists"
          item-orientation="horizontal"
          handle=".list-name"
          @reorder="onListReorder"
        > -->
      <div
        v-for="list in lists"
        :key="list.id"
        class="list"
        :class="{ 'collapsed': list.collapsed }"
      >
        <div>
          <div
            class="list-title"
          >
            <q-btn
              round
              flat
              icon="mdi-chevron-right"
              :class="{ 'collapse-btn': true, 'rotated': list.collapsed }"
              @click="onCollapsedStatus(list)"
            />
            <span class="list-name">{{ list.name }}</span>
            <q-badge
              rounded
              :style="{ backgroundColor: list.color }"
              class="q-ml-sm"
            />
          </div>
        </div>
        <dx-scroll-view
          class="scrollable-list"
          show-scrollbar="always"
        >
          <dx-sortable
            :key="list.id"
            :data="list.events"
            class="sortable-cards"
            group="tasksGroup"
            @drag-start="onTaskDragStart($event)"
            @drag-end="onTaskDragEnd($event)"
          >
            <div
              v-for="task in list.events"
              :key="task.data.id"
              class="q-card-container"
            >
              <transition
                name="fade-task"
                tag="div"
              >
                <q-card
                  v-show="task.visible"
                  bordered
                  :class="{ 'no-move': isInvoicedStatus(task.data.document_progress_status) }"
                >
                  <q-card-section>
                    <div class="event-title">
                      <reference-link
                        :data="{...task.data, number: task.data.number_mnemonic}"
                        :text="task.title"
                        :query-fields="['series', 'number']"
                        :target="getReferenceLinkTarget(task.data)"
                      >
                        <information-tool-tip :data="extractReferenceData(task.data)" />
                      </reference-link>
                    </div>

                    <div class="event-subtitle q-mt-sm">
                      <q-icon
                        name="mdi-account"
                        class="q-mr-xs"
                      />{{ task.customerName }}
                    </div>

                    <div
                      v-if="task.date"
                      :class="{ 'event-datetime': true, 'text-weight-bold': sortFilter === 0}"
                    >
                      <q-icon
                        name="mdi-calendar"
                        class="q-mr-xs"
                      />{{ task.date }}
                    </div>
                    <div :class="{'q-mt-sm': true, 'text-weight-bold': sortFilter === 1}">
                      <q-icon
                        name="mdi-sort-variant"
                        class="q-mr-xs"
                      />{{ i18n.t('common.priority') }} {{ task.data.order }}
                    </div>
                    <div
                      v-if="task.expectedDeliveryDate"
                      class="text-orange text-weight-bold"
                    >
                      <q-icon
                        name="mdi-alert"
                        class="q-mr-xs"
                      />{{ i18n.t('common.expectedDeliveryDateAlt') }} {{ task.expectedDeliveryDate }}
                    </div>
                    <div
                      v-if="task.intervals"
                      class="q-mt-sm"
                      :class="{ 'event-datetime': true, 'text-weight-bold': sortFilter === 0}"
                    >
                      <q-expansion-item
                        v-if="task.intervals.length > 0"
                        dense
                        expand-separator
                        :label="i18n.t('common.detail', 2)"
                      >
                        <q-card>
                          <q-card-section>
                            <div
                              v-for="interval in task.intervals"
                              :key="interval.id"
                              :class="{ 'text-grey': moment(interval.start).isBefore(moment(), 'day') }"
                            >
                              <q-icon
                                name="mdi-calendar"
                                class="q-mr-xs"
                              />{{ moment(interval.start).format('DD/MM') }}
                              <q-icon
                                name="mdi-clock"
                                class="q-mr-xs"
                              />{{ moment(interval.start).format('HH:mm') }} - {{ moment(interval.end).format('HH:mm') }}&nbsp;
                            </div>
                          </q-card-section>
                        </q-card>
                      </q-expansion-item>
                    </div>
                  </q-card-section>
                </q-card>
              </transition>
            </div>
          </dx-sortable>
        </dx-scroll-view>
      </div>
      <!-- </DxSortable>
      </DxScrollView> -->
    </div>
  </div>
</template>
<style scoped>

.fade-task-enter-active,
.fade-task-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.fade-task-enter-from,
.fade-task-leave-to {
  opacity: 0;
}

.fade-task-move {
  transition: transform 0.5s ease;
}

.header {
  display: flex;
  justify-content: end;
  margin-right: 1rem;
}

#kanban {
  white-space: nowrap;
  margin-top: 0rem;
  display: flex;
  width: 100%;
  margin-top: .5rem;
  height: calc(100vh - 10rem);
}

.list {
  transition: width 0.5s ease;
  padding: 0 .5rem 1.5rem .5rem;
  border-radius: .5rem;
  margin: .5rem;
  background-color: rgba(192, 192, 192, 0.2);
  display: flex;
  flex-direction: column;
  white-space: normal;
  width: v-bind(columnWidth);

  &.collapsed {
    width: 4rem !important;

    & .list-title {
      padding: 0;
      width: 100%;

      & .q-badge {
        display: none;
      }

      & .list-name {
        display: block;
        transform: rotate(90deg);
        white-space: nowrap;
        text-orientation: mixed;
        padding-left: 1rem;
      }
    }

    & .sortable-cards .q-card-container {
      transform: scale(0);
      opacity: 0;
      display: none;
    }
  }
}

.list-title {
  font-size: 1.2rem;
  text-align: center;
  padding: .5rem;
  cursor: pointer;

  &.collapsed {
    padding: 0;
  }
}

.list-name {
  transition: transform 0.5s ease;
  display: inline-block;
  transform-origin: left middle;
}

.sortable-cards {
  min-height: 30rem;
}

.q-card-container {
  transition: opacity .5s ease;
  cursor: pointer;
  user-select: none;
  margin-bottom: .5rem;

  &.no-move {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.collapse-btn {
  transition: transform 0.3s ease;

  &.rotated {
    transform: rotate(90deg);
  }
}

.event-title {
  font-weight: bold;
}

.event-subtitle {
  font-size: 0.9rem;
}

.event-datetime {
  /* text-align: end; */
}

</style>
